import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FileHeart, Loader2, Upload, X, Check, Download, History, Plus, Sparkles, BookOpenText, FileOutput, Activity } from "lucide-react";
import { AiSummaryPanel, AiFirmToggle } from "@/components/AiMedicalSummary";
import AiCaseDigest from "@/components/AiCaseDigest";
import ExportCenter from "@/components/ExportCenter";
import MedicalCommandCenter from "@/components/MedicalCommandCenter";
import { CaseDocSyncBanner } from "@/components/EnterpriseCrm";

export const STATUS_TONE = {
  chart_in_progress: "bg-slate-100 text-slate-500", chart_completed: "bg-cobalt-light text-cobalt",
  chart_uploaded: "bg-cobalt-light text-cobalt", chart_delivered_to_attorney: "bg-amber-50 text-amber-600",
  attorney_confirmed_receipt: "bg-emerald-50 text-emerald-600",
  additional_info_requested: "bg-red-50 text-red-600", chart_corrected_resubmitted: "bg-indigo-50 text-indigo-600",
};
const DOC_TYPES = [["initial_evaluation", "Initial evaluation"], ["follow_up_visit", "Follow-up visit"],
  ["discharge_summary", "Discharge summary"], ["specialist_report", "Specialist report"],
  ["corrected_report", "Corrected report"], ["imaging_referral", "Imaging referral"], ["other", "Other"]];

const StatusPill = ({ status, label }) => (
  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_TONE[status] || "bg-slate-100 text-slate-500"}`}>
    {label || (status || "").replace(/_/g, " ")}
  </span>
);

const download = async (rec) => {
  try {
    const res = await api.get(`/medical-records/${rec.record_id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${rec.document_type}_v${rec.current_version}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
};

/* ---------- provider chart panel (per appointment) ---------- */
export const ProviderChartPanel = ({ appointment, onClose }) => {
  const [rows, setRows] = useState(null);
  const [docType, setDocType] = useState("initial_evaluation");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/medical-records?appointment_id=${appointment.appointment_id}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [appointment.appointment_id]);
  useEffect(() => { load(); }, [load]);

  const createRecord = async () => {
    setBusy(true);
    try {
      await api.post("/medical-records", { case_id: appointment.case_id, appointment_id: appointment.appointment_id, document_type: docType });
      toast.success("Chart record started (Chart In Progress)"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const upload = async (rec, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/medical-records/${rec.record_id}/upload`, fd); toast.success("Chart uploaded & delivered to the attorney"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
  };
  const setStatus = async (rec, status) => {
    try { await api.post(`/medical-records/${rec.record_id}/status`, { status }); toast.success("Chart status updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="provider-chart-panel">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-navy flex items-center gap-2"><FileHeart className="w-5 h-5 text-cobalt" /> Charts — {appointment.case_number}</h3>
          <button onClick={onClose} data-testid="chart-panel-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-xs text-slate-400 mt-1">A chart status is required before the visit can be closed. Uploads go to the client's secure evidence vault and are delivered to the attorney automatically. Records are permanent and fully audited.</p>
        <CaseDocSyncBanner caseId={appointment.case_id} audience="provider" className="mt-3" />

        <div className="mt-4 flex gap-2 items-center">
          <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="chart-doc-type" className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
            {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button onClick={createRecord} disabled={busy} data-testid="chart-create" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-navy text-white text-xs font-semibold disabled:opacity-50">
            <Plus className="w-3.5 h-3.5" /> Start chart record
          </button>
        </div>

        {rows === null ? <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div> :
          rows.length === 0 ? <div className="py-8 text-center text-sm text-slate-400" data-testid="chart-empty">No chart records yet for this visit.</div> : (
          <div className="mt-4 space-y-3">
            {rows.map((r) => (
              <div key={r.record_id} className="rounded-2xl border border-slate-100 p-4" data-testid={`chart-record-${r.record_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-navy">{(r.document_type || "").replace(/_/g, " ")} <span className="text-[10px] text-slate-400 font-normal">v{r.current_version}</span></div>
                    <div className="text-[10px] text-slate-400">Updated {new Date(r.updated_at).toLocaleString()}</div>
                  </div>
                  <StatusPill status={r.status} label={r.status_label} />
                </div>
                {r.status === "additional_info_requested" && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2" data-testid={`chart-info-note-${r.record_id}`}>
                    Attorney requested: {(r.status_history || []).filter((h) => h.status === "additional_info_requested").slice(-1)[0]?.note || "additional documentation"}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cobalt text-white text-xs font-semibold cursor-pointer">
                    <Upload className="w-3.5 h-3.5" /> {r.current_version ? "Upload new version" : "Upload chart PDF"}
                    <input type="file" accept="application/pdf,image/*" className="hidden" data-testid={`chart-upload-${r.record_id}`}
                      onChange={(e) => upload(r, e.target.files?.[0])} />
                  </label>
                  {r.status === "chart_in_progress" && <button onClick={() => setStatus(r, "chart_completed")} data-testid={`chart-complete-${r.record_id}`} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Mark completed</button>}
                  {r.status === "additional_info_requested" && r.current_version > 0 && <button onClick={() => setStatus(r, "chart_corrected_resubmitted")} data-testid={`chart-resubmit-${r.record_id}`} className="px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold">Mark corrected / resubmitted</button>}
                  {r.current_version > 0 && <button onClick={() => download(r)} data-testid={`chart-download-${r.record_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs"><Download className="w-3 h-3" /> Download</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- attorney medical records center ---------- */
export const AttorneyMedicalRecords = () => {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [aiOpen, setAiOpen] = useState({});
  const [digestCase, setDigestCase] = useState(null);
  const [exportCase, setExportCase] = useState(null);
  const [commandCase, setCommandCase] = useState(null);
  const load = useCallback(() => api.get("/medical-records").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const ack = async (r) => {
    try { await api.post(`/medical-records/${r.record_id}/acknowledge`); toast.success("Receipt confirmed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const requestInfo = async (r) => {
    const note = window.prompt("Describe the revisions or additional documentation needed:");
    if (!note) return;
    try { await api.post(`/medical-records/${r.record_id}/request-info`, { note }); toast.success("Provider notified"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const openDetail = async (r) => {
    try { const { data } = await api.get(`/medical-records/${r.record_id}`); setDetail(data); } catch { toast.error("Could not load record"); }
  };
  if (rows === null) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const fresh = rows.filter((r) => ["chart_delivered_to_attorney", "chart_corrected_resubmitted", "chart_uploaded"].includes(r.status));
  const cases = [...new Map(rows.map((r) => [r.case_id, r.case_number])).entries()];
  return (
    <div data-testid="attorney-medical-records">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {cases.map(([cid, cnum]) => (
            <span key={cid} className="flex gap-1">
              <button onClick={() => setCommandCase({ caseId: cid, caseNumber: cnum })} data-testid={`command-open-${cid}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                <Activity className="w-3.5 h-3.5" /> Command
              </button>
              <button onClick={() => setDigestCase({ caseId: cid, caseNumber: cnum })} data-testid={`digest-open-${cid}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold">
                <BookOpenText className="w-3.5 h-3.5" /> Digest: {cnum}
              </button>
              <button onClick={() => setExportCase({ caseId: cid, caseNumber: cnum })} data-testid={`export-open-${cid}`}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-navy text-white text-xs font-semibold">
                <FileOutput className="w-3.5 h-3.5" /> Export
              </button>
            </span>
          ))}
        </div>
        <AiFirmToggle />
      </div>
      {fresh.length > 0 && (
        <div className="mb-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-700 font-semibold" data-testid="new-records-banner">
          New Medical Records Available — {fresh.length} chart(s) awaiting your receipt confirmation.
        </div>
      )}
      {rows.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-testid="amr-empty">No medical records delivered yet.</div> : (
        <div className="divide-y divide-slate-50">
          {rows.map((r) => {
            const v = (r.versions || []).slice(-1)[0];
            return (
              <div key={r.record_id} className="py-3" data-testid={`amr-row-${r.record_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-navy">{r.provider_name} — {(r.document_type || "").replace(/_/g, " ")}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {r.case_number} · v{r.current_version}{v ? ` · ${(v.size / 1024).toFixed(0)} KB · uploaded ${new Date(v.uploaded_at).toLocaleString()}` : " · no file yet"}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusPill status={r.status} label={r.status_label} />
                    {["chart_delivered_to_attorney", "chart_uploaded", "chart_corrected_resubmitted"].includes(r.status) &&
                      <button onClick={() => ack(r)} data-testid={`amr-ack-${r.record_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold"><Check className="w-3 h-3" /> Confirm receipt</button>}
                    {r.status !== "additional_info_requested" && r.current_version > 0 &&
                      <button onClick={() => requestInfo(r)} data-testid={`amr-request-${r.record_id}`} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs">Request info</button>}
                    {r.current_version > 0 && <button onClick={() => download(r)} data-testid={`amr-download-${r.record_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-600 text-xs"><Download className="w-3 h-3" /></button>}
                    <button onClick={() => openDetail(r)} data-testid={`amr-history-${r.record_id}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-400 text-xs"><History className="w-3 h-3" /></button>
                    {r.current_version > 0 && <button onClick={() => setAiOpen((o) => ({ ...o, [r.record_id]: !o[r.record_id] }))} data-testid={`amr-ai-${r.record_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold"><Sparkles className="w-3 h-3" /> AI</button>}
                  </div>
                </div>
                {aiOpen[r.record_id] && <AiSummaryPanel record={r} />}
              </div>
            );
          })}
        </div>
      )}
      {digestCase && <AiCaseDigest caseId={digestCase.caseId} caseNumber={digestCase.caseNumber} onClose={() => setDigestCase(null)} />}
      {exportCase && <ExportCenter caseId={exportCase.caseId} caseNumber={exportCase.caseNumber} onClose={() => setExportCase(null)} />}
      {commandCase && <MedicalCommandCenter caseId={commandCase.caseId} caseNumber={commandCase.caseNumber}
        onClose={() => setCommandCase(null)}
        onOpenExport={() => setExportCase({ caseId: commandCase.caseId, caseNumber: commandCase.caseNumber })}
        onOpenDigest={() => setDigestCase({ caseId: commandCase.caseId, caseNumber: commandCase.caseNumber })} />}
      {detail && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="amr-detail">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-navy text-sm">Audit history — {(detail.document_type || "").replace(/_/g, " ")}</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="mt-3 divide-y divide-slate-50">
              {(detail.audit || []).map((a) => (
                <div key={a.audit_id} className="py-1.5 text-[11px] flex justify-between gap-2">
                  <span className="text-navy font-medium">{a.action}{a.details?.version ? ` (v${a.details.version})` : ""}{a.details?.denied || a.details?.refused ? " — DENIED" : ""}<span className="text-slate-400 ml-1">{a.actor_name}</span></span>
                  <span className="text-slate-400 whitespace-nowrap">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- client read-only records list ---------- */
export const ClientMedicalRecords = ({ caseId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/medical-records?case_id=${caseId}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [caseId]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-4" data-testid={`client-medical-records-${caseId}`}>
      <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Medical records</div>
      {rows.map((r) => (
        <div key={r.record_id} className="py-1.5 flex flex-wrap items-center justify-between gap-2 border-b border-slate-50 last:border-0 text-sm">
          <span className="text-navy">{r.provider_name} — {(r.document_type || "").replace(/_/g, " ")} <span className="text-[10px] text-slate-400">v{r.current_version}</span></span>
          <StatusPill status={r.status} label={r.status_label} />
        </div>
      ))}
      <p className="text-[10px] text-slate-400 mt-1.5">Records are stored in your secure Evidence Vault under "Medical Records".</p>
    </div>
  );
};
